import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  HiOutlineMagnifyingGlass,
  HiOutlineArrowDownTray,
  HiOutlineTrash,
  HiOutlineDocumentText,
  HiOutlineFunnel,
  HiOutlineXMark,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2';
import {
  BsGrid3X3GapFill,
  BsListUl,
  BsFiletypePdf,
  BsFiletypePptx,
  BsFiletypeXlsx,
  BsFiletypeDocx,
  BsFiletypePng,
  BsFiletypeJpg,
  BsFileEarmark,
} from 'react-icons/bs';
import Spinner from '../../components/Spinner/Spinner';
import BrandFilesFilter from '../../components/BrandFilesFilter/BrandFilesFilter';
import { fetchAllBrandFiles, deleteBrandFile, getDownloadUrl, BrandFile, BrandFileFilters } from '../../services/brandFilesService';
import './AllFiles.css';

type ViewMode = 'grid' | 'list';

function getFileIcon(type: string) {
  if (type.includes('pdf')) return <BsFiletypePdf className="bf-file-icon bf-icon-pdf" />;
  if (type.includes('presentation') || type.includes('powerpoint')) return <BsFiletypePptx className="bf-file-icon bf-icon-pptx" />;
  if (type.includes('spreadsheet') || type.includes('excel')) return <BsFiletypeXlsx className="bf-file-icon bf-icon-xlsx" />;
  if (type.includes('word') || type.includes('document')) return <BsFiletypeDocx className="bf-file-icon bf-icon-docx" />;
  if (type.includes('png')) return <BsFiletypePng className="bf-file-icon bf-icon-img" />;
  if (type.includes('jpg') || type.includes('jpeg')) return <BsFiletypeJpg className="bf-file-icon bf-icon-img" />;
  if (type.includes('image')) return <BsFiletypePng className="bf-file-icon bf-icon-img" />;
  return <BsFileEarmark className="bf-file-icon bf-icon-default" />;
}

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function countActiveFilters(filters: BrandFileFilters): number {
  return Object.values(filters).filter(Boolean).length;
}

export default function AllFiles() {
  const [files, setFiles] = useState<BrandFile[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<BrandFileFilters>({});
  const filtersRef = useRef<BrandFileFilters>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination
  const PAGE_SIZE = 20;
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const hasMore = files.length < totalCount;

  const loadFiles = useCallback((filters: BrandFileFilters, isInitial = false) => {
    if (isInitial) setInitialLoading(true);
    else setFiltering(true);
    fetchAllBrandFiles(PAGE_SIZE, 0, filters)
      .then((result) => {
        setFiles(result.files);
        setTotalCount(result.totalCount);
      })
      .finally(() => {
        setInitialLoading(false);
        setFiltering(false);
      });
  }, []);

  useEffect(() => {
    loadFiles({}, true);
  }, [loadFiles]);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchAllBrandFiles(PAGE_SIZE, files.length, filtersRef.current);
      setFiles((prev) => [...prev, ...result.files]);
      setTotalCount(result.totalCount);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleApplyFilters = (filters: BrandFileFilters) => {
    setAppliedFilters(filters);
    filtersRef.current = filters;
    setFilterOpen(false);
    loadFiles(filters);
  };

  const activeFilterCount = countActiveFilters(appliedFilters);

  const filteredFiles = useMemo(() => {
    return files.filter((f) => {
      const matchesSearch =
        searchQuery === '' ||
        f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.uploadedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.brandName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [files, searchQuery]);

  const formatDate = useCallback((dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  const handleDeleteClick = (fileId: string, fileName: string) => {
    setDeleteConfirm({ id: fileId, name: fileName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const success = await deleteBrandFile(deleteConfirm.id);
      if (success) {
        setFiles((prev) => prev.filter((f) => f.id !== deleteConfirm.id));
        setTotalCount((prev) => prev - 1);
      }
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleDownload = async (fileId: string, fileName: string) => {
    const url = await getDownloadUrl(fileId);
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (initialLoading) {
    return (
      <div className="all-files-page" style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <Spinner size="small" />
      </div>
    );
  }

  return (
    <div className="all-files-page">
      <div className="af-page-header">
        <h1 className="af-page-title">Files</h1>
        <p className="af-page-subtitle">All brand files across your organization</p>
      </div>

      {/* Header: Search + Filter + View Toggle */}
      <div className="bf-header">
        <div className="bf-search-bar">
          <HiOutlineMagnifyingGlass className="bf-search-icon" />
          <input
            type="text"
            placeholder="Search by file name, brand, or uploader..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bf-search-input"
          />
        </div>
        <div className="bf-header-right">
          <button className="bf-filter-btn" onClick={() => setFilterOpen(true)}>
            <HiOutlineFunnel />
            Filters
            {activeFilterCount > 0 && <span className="bf-filter-badge">{activeFilterCount}</span>}
          </button>
          <div className="bf-view-toggle">
            <button
              className={`bf-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <BsGrid3X3GapFill />
            </button>
            <button
              className={`bf-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <BsListUl />
            </button>
          </div>
        </div>
      </div>

      {/* File Count */}
      {filteredFiles.length > 0 && (
        <p className="bf-file-count">
          {filteredFiles.length} of {totalCount} file{totalCount !== 1 ? 's' : ''}
        </p>
      )}

      {/* Content */}
      {filtering ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner size="small" />
        </div>
      ) : filteredFiles.length > 0 ? (
        viewMode === 'grid' ? (
          /* ===== GRID VIEW ===== */
          <div className="bf-grid">
            {filteredFiles.map((file) => (
              <div key={file.id} className="bf-card">
                <div className="bf-card-icon-area">
                  {getFileIcon(file.type)}
                  <span className="bf-card-ext">{getFileExtension(file.name)}</span>
                </div>
                <div className="bf-card-body">
                  <p className="bf-card-name" title={file.name}>{file.name}</p>
                  {file.description && (
                    <p className="bf-card-description" title={file.description}>{file.description}</p>
                  )}
                  <span className="bf-card-brand-tag">{file.brandName}</span>
                  <div className="bf-card-meta">
                    <span className="bf-card-size">{formatFileSize(file.size)}</span>
                    <span className="bf-card-dot"></span>
                    <span className="bf-card-date">{formatDate(file.uploadedAt)}</span>
                  </div>
                  <div className="bf-card-footer">
                    <span className="bf-card-uploader">{file.uploadedBy}</span>
                    <div className="bf-card-actions">
                      <button className="bf-icon-btn" title="Download" onClick={() => handleDownload(file.id, file.name)}><HiOutlineArrowDownTray /></button>
                      <button className="bf-icon-btn bf-delete-btn" title="Delete" onClick={() => handleDeleteClick(file.id, file.name)}><HiOutlineTrash /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ===== LIST VIEW ===== */
          <div className="bf-list-wrapper">
            <table className="bf-list-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>Description</th>
                  <th>Size</th>
                  <th>Uploaded By</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => (
                  <tr key={file.id}>
                    <td>
                      <div className="bf-list-name">
                        {getFileIcon(file.type)}
                        <span>{file.name}</span>
                      </div>
                    </td>
                    <td><span className="af-brand-cell">{file.brandName}</span></td>
                    <td>
                      <span className="bf-list-description">{file.description || '—'}</span>
                    </td>
                    <td>{formatFileSize(file.size)}</td>
                    <td>{file.uploadedBy}</td>
                    <td>{formatDate(file.uploadedAt)}</td>
                    <td>
                      <div className="bf-list-actions">
                        <button className="bf-icon-btn" title="Download" onClick={() => handleDownload(file.id, file.name)}><HiOutlineArrowDownTray /></button>
                        <button className="bf-icon-btn bf-delete-btn" title="Delete" onClick={() => handleDeleteClick(file.id, file.name)}><HiOutlineTrash /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="bf-empty-state">
          <HiOutlineDocumentText className="bf-empty-icon" />
          <h3 className="bf-empty-title">No Files</h3>
          <p className="bf-empty-desc">
            {searchQuery || activeFilterCount > 0
              ? 'No files match your search or filter criteria.'
              : 'No brand files have been uploaded yet.'}
          </p>
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="bf-load-more">
          <button
            className="bf-load-more-btn"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : `Load More (${files.length} of ${totalCount})`}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="bf-overlay" onClick={() => { if (!deleting) setDeleteConfirm(null); }}>
          <div className="bf-modal bf-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bf-modal-header">
              <h3>Delete File</h3>
              <button className="bf-modal-close" onClick={() => { if (!deleting) setDeleteConfirm(null); }}>
                <HiOutlineXMark />
              </button>
            </div>
            <div className="bf-modal-body">
              <div className="bf-delete-confirm-content">
                <HiOutlineExclamationTriangle className="bf-delete-warn-icon" />
                <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
                <p className="bf-delete-hint">This action cannot be undone.</p>
              </div>
            </div>
            <div className="bf-modal-footer">
              <button className="bf-cancel-btn" onClick={() => setDeleteConfirm(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="bf-submit-btn bf-delete-confirm-btn" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel */}
      <BrandFilesFilter
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        showBrandFilter={true}
        appliedFilters={appliedFilters}
      />
    </div>
  );
}
